'use client';

import { useDisclosure } from '@heroui/react';
import { AxiosError } from 'axios';
import { type ChangeEvent, type SubmitEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import useHandleInputChange from '@/hooks/use-handle-input-change';
import { api } from '@/lib/providers/axios/api';
import { API_ROUTES } from '@/shared/constants/api-routes';
import { buildApiRoute } from '@/shared/functions/build-api-route';
import { formatPhoneNumber } from '@/shared/functions/format-phone-number';
import removeNonNumericCharacters from '@/shared/functions/remove-non-numeric-characters';
import { handleErrorTreatment } from '@/shared/functions/zod/get-zod-errors';
import type { CreatePreRegistrationForm } from '../interfaces/create-pre-registration-form';
import { CreatePreRegistrationSchema } from '../schemas/create-pre-registration-schema';
export type PreRegisterFormState = CreatePreRegistrationForm & {
  acceptedLgpd: boolean;
};
export type PreRegisterSubmitStatus = 'idle' | 'success' | 'already_exists';
const INITIAL_PRE_REGISTRATION: PreRegisterFormState = {
  name: '',
  email: '',
  address: '',
  cep: '',
  contact: '',
  neighborhood: '',
  city: '',
  state: '',
  stateCode: '',
  ibge: '',
  profession: 'ARQUITETO(A)',
  acceptedLgpd: false
};
export function usePreRegisterForm() {
  const addAddressModal = useDisclosure();
  const [preRegistration, setPreRegistration] = useState<PreRegisterFormState>(INITIAL_PRE_REGISTRATION);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<PreRegisterSubmitStatus>('idle');
  const resetForm = useCallback(() => {
    setPreRegistration(INITIAL_PRE_REGISTRATION);
    setSubmitStatus('idle');
  }, []);
  const hasAddress = useMemo(() => {
    return Boolean(preRegistration.cep?.trim() && preRegistration.ibge?.trim() && preRegistration.address?.trim() && preRegistration.neighborhood?.trim() && preRegistration.city?.trim() && preRegistration.state?.trim() && preRegistration.stateCode?.trim());
  }, [preRegistration]);
  const canSubmit = useMemo(() => {
    if (!preRegistration.acceptedLgpd) return false;
    const {
      acceptedLgpd: _acceptedLgpd,
      ...formData
    } = preRegistration;
    return CreatePreRegistrationSchema.safeParse(formData).success;
  }, [preRegistration]);
  const handleInputChange = useHandleInputChange(setPreRegistration);
  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const digits = removeNonNumericCharacters(e.target.value);
    if (!digits) {
      handleInputChange('contact', '');
      return;
    }
    const national = (digits.startsWith('55') ? digits.slice(2) : digits).slice(0, 11);
    if (national.length === 10 || national.length === 11) {
      handleInputChange('contact', `55${national}`);
      return;
    }
    handleInputChange('contact', national);
  }, [handleInputChange]);
  const handleSubmit = useCallback(async (e_0: SubmitEvent<HTMLFormElement>) => {
    e_0.preventDefault();
    if (!preRegistration.acceptedLgpd) {
      toast.error('É necessário aceitar os termos de uso dos dados para continuar.');
      return;
    }
    setIsLoading(true);
    try {
      const {
        acceptedLgpd: _acceptedLgpd_0,
        ...formData_0
      } = preRegistration;
      const cepDigits = removeNonNumericCharacters(formData_0.cep).slice(0, 8);
      const payload = {
        name: formData_0.name,
        email: formData_0.email,
        contact: formData_0.contact,
        profession: formData_0.profession,
        cep: cepDigits,
        address: formData_0.address,
        neighborhood: formData_0.neighborhood,
        city: formData_0.city,
        state: formData_0.state,
        stateCode: formData_0.stateCode,
        ibge: formData_0.ibge
      };
      const parsedForm = await CreatePreRegistrationSchema.parseAsync(payload);
      await api.post(buildApiRoute(API_ROUTES.POST.PUBLIC.CREATE_PRE_REGISTRATION), parsedForm);
      setSubmitStatus('success');
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 409) {
        setSubmitStatus('already_exists');
        return;
      }
      handleErrorTreatment(err);
    } finally {
      setIsLoading(false);
    }
  }, [preRegistration]);
  return {
    addAddressModal,
    preRegistration,
    setPreRegistration,
    submitStatus,
    resetForm,
    hasAddress,
    canSubmit,
    handleInputChange,
    handlePhoneChange,
    handleSubmit,
    isLoading,
    formatPhoneNumber
  };
}